<script setup>
import { computed, ref, watch } from 'vue';
import {
  catalogKeyForParticipant,
  participantsForSection,
} from '../composables/salesReviewReturnSections';
import SalesReviewReturnPartyHeader from './SalesReviewReturnPartyHeader.vue';
import SalesReviewReturnPartyItem from './SalesReviewReturnPartyItem.vue';
import SalesReviewReturnPartyObservation from './SalesReviewReturnPartyObservation.vue';

const props = defineProps({
  definition: { type: Object, required: true },
  items: { type: Array, default: () => [] },
  participants: { type: Array, default: () => [] },
  catalog: { type: Object, default: () => ({}) },
  readOnly: { type: Boolean, default: false },
  showErrors: { type: Boolean, default: false },
});
const emit = defineEmits(['change']);
const expanded = ref(props.items.length > 0);
const sectionParticipants = computed(() =>
  participantsForSection(props.participants, props.definition.id)
);
const enabled = computed(() => props.items.length > 0);
const correctionCount = computed(() =>
  props.items.reduce((total, item) => total + (item.checks || []).length, 0)
);
const observation = computed(() => props.items[0]?.observation || '');
const quantityOptions = computed(() =>
  Array.from(
    { length: sectionParticipants.value.length },
    (_, index) => index + 1
  )
);

watch(enabled, value => {
  if (value) expanded.value = true;
});

const emptyItem = () => ({
  section: props.definition.id,
  target_key: '',
  checks: [],
  observation: observation.value,
});
const clonedItems = count =>
  Array.from({ length: count }, (_, index) => ({
    ...(props.items[index] || emptyItem()),
    section: props.definition.id,
    checks: [...(props.items[index]?.checks || [])],
  }));
const emitItems = nextItems => emit('change', nextItems);
const participantFor = item =>
  props.participants.find(
    participant => String(participant.id) === String(item.target_key)
  );
const optionsFor = item =>
  props.catalog[
    catalogKeyForParticipant(props.definition.id, participantFor(item))
  ] || [];
const itemIncomplete = item =>
  !item.target_key || !item.checks?.length || !item.observation?.trim();
const hasIncompleteItem = computed(() => props.items.some(itemIncomplete));

const toggleEnabled = event => {
  if (event.target.checked) {
    expanded.value = true;
    emitItems([emptyItem()]);
    return;
  }

  emitItems([]);
};
const updateCount = event => emitItems(clonedItems(Number(event.target.value)));
const updateTarget = (index, value) => {
  const nextItems = clonedItems(props.items.length);
  nextItems[index] = { ...nextItems[index], target_key: value, checks: [] };
  emitItems(nextItems);
};
const updateChecks = (index, ids) => {
  const nextItems = clonedItems(props.items.length);
  nextItems[index] = {
    ...nextItems[index],
    checks: optionsFor(nextItems[index]).filter(option =>
      ids.includes(String(option.id ?? option.id_correcao ?? option))
    ),
  };
  emitItems(nextItems);
};
const updateObservation = value =>
  emitItems(
    clonedItems(props.items.length).map(item => ({
      ...item,
      observation: value,
    }))
  );
const toggleExpanded = () => {
  expanded.value = !expanded.value;
};
</script>

<template>
  <section
    class="overflow-hidden rounded-lais border bg-white"
    :class="enabled ? 'border-lais-primary' : 'border-lais-border-soft'"
  >
    <SalesReviewReturnPartyHeader
      :correction-count="correctionCount"
      :definition="definition"
      :enabled="enabled"
      :expanded="expanded"
      :participants-count="sectionParticipants.length"
      :read-only="readOnly"
      @toggle="toggleEnabled"
      @toggle-expanded="toggleExpanded"
    />

    <div
      v-if="enabled && expanded"
      class="grid gap-4 border-t border-lais-border-soft p-4"
    >
      <label class="grid gap-2 text-sm font-medium text-lais-text">
        <span>{{ definition.quantityLabel }}</span>
        <select
          class="h-10 rounded-lais-sm border border-lais-border bg-white px-3 text-sm"
          :value="items.length"
          :disabled="readOnly"
          @change="updateCount"
        >
          <option v-for="count in quantityOptions" :key="count" :value="count">
            {{ count }}
          </option>
        </select>
      </label>

      <SalesReviewReturnPartyItem
        v-for="(item, index) in items"
        :key="item.id || `${definition.id}-${index}`"
        :definition="definition"
        :index="index"
        :item="item"
        :options="optionsFor(item)"
        :participants="sectionParticipants"
        :read-only="readOnly"
        :show-errors="showErrors"
        @checks="updateChecks"
        @target="updateTarget"
      />

      <SalesReviewReturnPartyObservation
        :definition="definition"
        :has-incomplete-item="hasIncompleteItem"
        :observation="observation"
        :read-only="readOnly"
        :show-errors="showErrors"
        @update="updateObservation"
      />
    </div>
  </section>
</template>
